import { Modal } from "antd";
import React, { useRef } from "react";
import { primaryColor } from "../../utils/constants";
import {
  CameraOutlined,
  FolderOpenOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";

export const ImageUploadModel = ({
  showImageModal,
  setShowImageModal,
  onSelectImage,
  setImagePreview,
}) => {
  // Refs
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleImageChange = (file) => {
    onSelectImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  // Handle camera capture
  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.style.display = "block";
        videoRef.current.play();

        // Hide upload options and show camera controls
        const uploadOptions = document.getElementById("upload-options");
        const controls = document.getElementById("camera-controls");

        if (uploadOptions) {
          uploadOptions.style.display = "none";
        }
        if (controls) {
          controls.style.display = "flex";
        }
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Unable to access camera. Please check permissions.");
    }
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], "camera-photo.jpg", {
            type: "image/jpeg",
          });
          handleImageChange(file);
          stopCamera();
          setShowImageModal(false);
        },
        "image/jpeg",
        0.8
      );
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
      videoRef.current.style.display = "none";

      // Show upload options and hide camera controls
      const uploadOptions = document.getElementById("upload-options");
      const controls = document.getElementById("camera-controls");

      if (uploadOptions) {
        uploadOptions.style.display = "block";
      }
      if (controls) {
        controls.style.display = "none";
      }
    }
  };

  // Handle file upload from computer
  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Handle file input change
  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageChange(file);
      setShowImageModal(false);
    }
  };

  return (
    <div>
      {/* Image Upload Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 pb-2">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-xl shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}25 100%)`,
                border: `1px solid ${primaryColor}20`,
              }}
            >
              <UploadOutlined
                style={{ color: primaryColor, fontSize: "20px" }}
              />
            </div>
            <div>
              <span className="text-xl font-semibold text-gray-800">
                Update Profile Photo
              </span>
              <div className="text-sm text-gray-500 mt-1">
                Choose your preferred method
              </div>
            </div>
          </div>
        }
        open={showImageModal}
        onCancel={() => {
          setShowImageModal(false);
          stopCamera();
        }}
        footer={null}
        centered
        width={600}
        className="image-upload-modal"
        styles={{
          header: {
            borderBottom: `2px solid ${primaryColor}15`,
            paddingBottom: "20px",
            marginBottom: "24px",
          },
        }}
      >
        <div className="py-2">
          {/* Camera Preview Section */}
          <div className="mb-6" id="camera-preview-container">
            <video
              ref={videoRef}
              className="w-full rounded-xl shadow-lg border-2 border-gray-200"
              style={{
                maxHeight: "350px",
                display: "none",
                objectFit: "cover",
              }}
              autoPlay
              playsInline
            />

            <div
              className="mt-4 flex justify-center gap-4"
              id="camera-controls"
              style={{ display: "none" }}
            >
              <button
                onClick={capturePhoto}
                className="flex items-center gap-3 px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                }}
              >
                <CameraOutlined className="text-lg" />
                Capture Photo
              </button>
              <button
                onClick={stopCamera}
                className="flex items-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Options Section */}
          <div id="upload-options">
            <p className="text-gray-600 mb-6 text-center text-lg">
              How would you like to update the profile photo?
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Camera Option */}
              <div
                onClick={handleCameraCapture}
                className="group cursor-pointer p-6 rounded-2xl border-2 border-gray-200 hover:border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <CameraOutlined className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      Take Photo
                    </h3>
                    <p className="text-sm text-gray-600">
                      Use your camera to capture a new photo
                    </p>
                  </div>
                  <div className="w-full h-1 bg-blue-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>

              {/* Upload Option */}
              <div
                onClick={handleFileUpload}
                className="group cursor-pointer p-6 rounded-2xl border-2 border-gray-200 hover:border-green-400 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <FolderOpenOutlined className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      Upload Photo
                    </h3>
                    <p className="text-sm text-gray-600">
                      Choose an existing photo from your device
                    </p>
                  </div>
                  <div className="w-full h-1 bg-green-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            </div>

            {/* Tips Section */}
            <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg
                    className="w-4 h-4 text-yellow-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">
                    Photo Guidelines
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Always use a clear photo where your face is easily visible,
                    as it helps build trust and credibility with others.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {/* Hidden canvas for camera capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};
